import { useEffect, useState } from "react";
import {
  FileText, FileSignature, QrCode, Download, MessageCircle,
  AlertCircle, ArrowRight, X, Loader2,
} from "lucide-react";
import { PageHero } from "../components/PageHero";
import { fetchPublic } from "../lib/content";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { BRAND, WA_LINK } from "../lib/constants";

const ActionCard = ({ icon: Icon, eyebrow, title, helper, ctaLabel, available, onClick, href, testId }) => (
  <div data-testid={testId} className={`group bg-white border border-slate-200 rounded-3xl p-7 md:p-8 hover:-translate-y-1 hover:shadow-xl hover:shadow-[#0A192F]/5 transition-all flex flex-col ${available ? "" : "opacity-70"}`}>
    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#00B37E]/15 to-[#0A192F]/10 flex items-center justify-center">
      <Icon className="w-7 h-7 text-[#00B37E]" />
    </div>
    <div className="mt-5 text-[11px] uppercase tracking-[0.22em] font-bold text-[#00B37E]">{eyebrow}</div>
    <h3 className="mt-2 font-heading text-xl font-bold text-[#0A192F] leading-snug">{title}</h3>
    {helper ? <p className="mt-2.5 text-sm text-[#64748B] leading-relaxed flex-1">{helper}</p> : <div className="flex-1" />}

    {available ? (
      href ? (
        <a href={href} target="_blank" rel="noopener noreferrer" data-testid={`${testId}-action`}>
          <Button className="mt-6 w-full bg-[#0A192F] hover:bg-[#0F2648] text-white rounded-full h-11 font-semibold">
            {ctaLabel} <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </a>
      ) : (
        <Button onClick={onClick} data-testid={`${testId}-action`} className="mt-6 w-full bg-[#0A192F] hover:bg-[#0F2648] text-white rounded-full h-11 font-semibold">
          {ctaLabel} <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      )
    ) : (
      <Button disabled className="mt-6 w-full bg-slate-100 text-slate-400 rounded-full h-11 font-semibold cursor-not-allowed" data-testid={`${testId}-disabled`}>
        Coming soon
      </Button>
    )}
  </div>
);

export default function Downloads() {
  const [s, setS] = useState(null);
  const [payOpen, setPayOpen] = useState(false);

  useEffect(() => {
    fetchPublic("settings").then(setS).catch(() => setS({}));
  }, []);

  const loading = s === null;

  return (
    <div data-testid="downloads-page">
      <PageHero
        eyebrow="Downloads"
        title="Brochure, admission form &amp; fee payment."
        desc="Read our full curriculum, apply offline by filling the admission form, or pay your fees online — all in one place."
        image="https://images.pexels.com/photos/4144222/pexels-photo-4144222.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1920"
        alt="TASSEI downloads"
      />

      <section className="py-16 md:py-20" data-testid="downloads-cards-section">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          {loading ? (
            <div className="flex items-center gap-2 text-[#64748B] justify-center py-10">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <ActionCard
                testId="card-brochure"
                icon={FileText}
                eyebrow="Brochure"
                title={s.brochure_label || "Download Brochure"}
                helper="See our complete curriculum, schedules and centre details at a glance."
                ctaLabel="Download PDF"
                available={!!s.brochure_url}
                href={s.brochure_url}
              />
              <ActionCard
                testId="card-admission"
                icon={FileSignature}
                eyebrow="Admission Form"
                title={s.admission_form_label || "Download Admission Form"}
                helper="Fill the form offline and submit at our centre or share it back on WhatsApp."
                ctaLabel="Download PDF"
                available={!!s.admission_form_url}
                href={s.admission_form_url}
              />
              <ActionCard
                testId="card-pay"
                icon={QrCode}
                eyebrow="Fees Payment"
                title={s.payment_title || "Pay Fees Online"}
                helper="Scan our official UPI QR and pay securely. Share the screenshot with our admin to confirm."
                ctaLabel="Show Payment QR"
                available={!!s.payment_qr_image}
                onClick={() => setPayOpen(true)}
              />
            </div>
          )}

          {/* Helper note */}
          {!loading && !(s.brochure_url || s.admission_form_url || s.payment_qr_image) ? (
            <div className="mt-10 text-center" data-testid="downloads-empty">
              <p className="text-[#64748B]">
                Downloads will appear here shortly. For any document, WhatsApp our team at{" "}
                <a href={WA_LINK("Hi TASSEI, please send me the brochure / admission form")} target="_blank" rel="noopener noreferrer" className="text-[#00B37E] font-semibold">
                  {BRAND.phone}
                </a>.
              </p>
            </div>
          ) : null}

          {!loading && (s.brochure_url || s.admission_form_url || s.payment_qr_image) ? (
            <div className="mt-12 bg-[#F8FAF9] border border-slate-200 rounded-2xl p-5 md:p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="w-11 h-11 rounded-xl bg-[#00B37E]/12 flex items-center justify-center flex-shrink-0">
                <MessageCircle className="w-5 h-5 text-[#00B37E]" />
              </div>
              <div className="flex-1 text-sm text-[#475569]">
                Need any document not listed here? Our admin staff will send it on WhatsApp instantly.
              </div>
              <a href={WA_LINK("Hi TASSEI, I need a document")} target="_blank" rel="noopener noreferrer" data-testid="downloads-help-whatsapp">
                <Button className="bg-[#00B37E] hover:bg-[#009166] text-white rounded-full h-10 px-5 font-semibold">
                  Chat with admin
                </Button>
              </a>
            </div>
          ) : null}
        </div>
      </section>

      {/* Payment dialog */}
      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent className="max-w-md p-0 overflow-hidden" data-testid="payment-dialog">
          <div className="bg-gradient-to-br from-[#0A192F] via-[#0F2648] to-[#00B37E] text-white px-6 pt-6 pb-8 relative">
            <div className="absolute inset-0 tassei-dot-bg opacity-20" />
            <DialogHeader className="relative text-left">
              <DialogTitle className="font-heading text-2xl font-extrabold text-white">
                {s?.payment_title || "Pay Fees Online"}
              </DialogTitle>
              <div className="text-xs uppercase tracking-[0.22em] font-bold text-[#7CF0BD] mt-1">
                Scan to pay
              </div>
            </DialogHeader>
            <div className="relative mt-5 bg-white rounded-2xl p-4 flex justify-center">
              {s?.payment_qr_image ? (
                <img src={s.payment_qr_image} alt="Payment QR" className="w-56 h-56 object-contain" data-testid="payment-qr-image" />
              ) : (
                <div className="w-56 h-56 flex items-center justify-center text-slate-400 text-sm">QR coming soon</div>
              )}
            </div>
            <div className="relative mt-5 flex items-start gap-2.5 bg-amber-400/15 border border-amber-300/30 rounded-xl p-3.5 text-sm leading-relaxed">
              <AlertCircle className="w-4 h-4 mt-0.5 text-amber-300 flex-shrink-0" />
              <span data-testid="payment-note">
                {s?.payment_note ||
                  "After paying, share the screenshot with our admin staff on WhatsApp for confirmation."}
              </span>
            </div>
          </div>
          <div className="px-6 py-4 bg-white flex gap-2">
            <a
              href={WA_LINK("Hi TASSEI, I just paid my fees online. Sharing the screenshot now.")}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="payment-whatsapp-cta"
              className="flex-1"
            >
              <Button className="w-full bg-[#00B37E] hover:bg-[#009166] text-white rounded-full h-11 font-semibold">
                <MessageCircle className="w-4 h-4 mr-2" /> Send Screenshot
              </Button>
            </a>
            <Button variant="outline" onClick={() => setPayOpen(false)} className="rounded-full h-11">
              <X className="w-4 h-4" />
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
